'use client';

/**
 * CTAButtons - Unified CTA button rendering
 * 
 * Renders primary and secondary call-to-action buttons with configurable styling.
 */

import React from 'react';
import Link from 'next/link';
import { CTAConfig, AnimationConfig } from '../types';
import { SmoothReveal } from '@/components/shared/SmoothReveal';
import { getBrandColor } from '@/config/brand-colors';
import { ArrowRight, ExternalLink } from 'lucide-react';

interface CTAButtonsProps {
  config?: CTAConfig;
  animation?: AnimationConfig;
  className?: string;
}

const sizeClasses: Record<string, string> = {
  sm: 'h-10 px-4 text-sm',
  md: 'h-12 px-6 text-base',
  lg: 'h-14 px-8 text-lg md:h-16 md:px-12',
};

export function CTAButtons({ config, animation, className = '' }: CTAButtonsProps) {
  if (!config || (!config.primary && !config.secondary)) return null;

  const { primary, secondary, alignment = 'left' } = config;

  const alignmentClasses: Record<string, string> = {
    left: 'justify-start',
    center: 'justify-center',
    right: 'justify-end',
  };

  const delay = animation?.delay || 0;

  const renderButton = (
    button: NonNullable<typeof primary>,
    variant: 'primary' | 'secondary'
  ) => {
    const isPrimary = variant === 'primary';
    const sizeClass = sizeClasses[button.size || 'md'];
    const isExternal = button.href.startsWith('http');

    const Icon = button.icon === 'arrow' ? ArrowRight : 
                 button.icon === 'external' ? ExternalLink : 
                 null;

    const baseClasses = `inline-flex items-center justify-center rounded-md font-medium transition-all duration-200 whitespace-nowrap ${sizeClass}`;

    const primaryStyles: React.CSSProperties = {
      backgroundColor: getBrandColor('primaryBlue'),
      color: getBrandColor('white'),
      border: 'none',
    };

    const secondaryStyles: React.CSSProperties = {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      borderColor: 'white',
      borderWidth: '2px',
      color: 'white',
    };

    const outlineStyles: React.CSSProperties = {
      backgroundColor: 'transparent',
      borderColor: getBrandColor('primaryBlue'),
      borderWidth: '2px',
      color: getBrandColor('primaryBlue'),
    };

    const ghostStyles: React.CSSProperties = {
      backgroundColor: 'transparent',
      color: getBrandColor('primaryBlue'),
    };

    const getStyles = () => {
      if (isPrimary) return primaryStyles;
      switch (button.variant) {
        case 'outline': return outlineStyles;
        case 'ghost': return ghostStyles;
        default: return secondaryStyles;
      }
    };

    const content = (
      <>
        {Icon && button.iconPosition === 'left' && <Icon className="mr-2 h-5 w-5" />}
        {button.text}
        {Icon && button.iconPosition !== 'left' && <Icon className="ml-2 h-5 w-5" />}
      </>
    );

    if (isExternal) {
      return (
        <a
          href={button.href}
          target="_blank"
          rel="noopener noreferrer"
          className={baseClasses}
          style={getStyles()}
        >
          {content}
        </a>
      );
    }

    return (
      <Link href={button.href} className={baseClasses} style={getStyles()}>
        {content}
      </Link>
    );
  };

  return (
    <SmoothReveal delay={delay}>
      <div className={`flex flex-col sm:flex-row gap-4 ${alignmentClasses[alignment]} ${className}`}>
        {primary && renderButton(primary, 'primary')}
        {secondary && renderButton(secondary, 'secondary')}
      </div>
    </SmoothReveal>
  );
}

export default CTAButtons;
